<template>
  <div class="tp-page" :style="{ '--tp-primario': tienda.colorPrimario || '#2563eb' }">
    <div v-if="cargando" class="tp-loading">
      <div class="tp-spinner"></div>
    </div>

    <div v-else-if="error" class="tp-error">
      <div style="font-size:40px; margin-bottom:12px;">🛍️</div>
      <div style="font-weight:700; margin-bottom:4px;">Tienda no disponible</div>
      <div style="font-size:13px; opacity:.7;">{{ error }}</div>
    </div>

    <!-- Selector de sucursal — solo cuando el negocio tiene más de una y todavía no se resolvió -->
    <div v-else-if="mostrarSelectorSucursal" class="tp-selector">
      <div class="tp-selector-inner">
        <div style="font-size:32px; margin-bottom:10px;">📍</div>
        <div style="font-weight:800; font-size:16px; margin-bottom:6px;">¿Desde qué sucursal comprás?</div>
        <div style="font-size:12px; color:#6b7280; margin-bottom:22px;">{{ tienda.nombre }} tiene {{ tienda.sucursales.length }} sucursales</div>

        <button class="tp-btn-primario" :disabled="resolviendoUbicacion" @click="usarUbicacion" style="margin-bottom:10px;">
          {{ resolviendoUbicacion ? 'Buscando tu ubicación…' : '📍 Usar mi ubicación' }}
        </button>
        <div v-if="errorUbicacion" style="font-size:11px; color:#dc2626; margin-bottom:14px;">{{ errorUbicacion }}</div>

        <div style="font-size:11px; color:#9ca3af; margin:14px 0 10px;">o elegí manualmente</div>
        <div style="display:flex; flex-direction:column; gap:8px;">
          <button v-for="s in tienda.sucursales" :key="s.id" class="tp-sucursal-opcion" @click="confirmarSucursal(s.id)">
            <div style="font-weight:700; font-size:13px;">{{ s.nombre }}</div>
            <div v-if="s.direccion" style="font-size:11px; color:#9ca3af; margin-top:2px;">{{ s.direccion }}</div>
          </button>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Portada -->
      <div class="tp-cover" :style="tienda.portadaUrl ? { backgroundImage: `url(${tienda.portadaUrl})` } : {}">
        <div class="tp-cover-overlay"></div>
        <img v-if="tienda.logoUrl" :src="tienda.logoUrl" class="tp-logo" />
      </div>

      <div class="tp-header">
        <div class="tp-nombre">{{ tienda.nombre }}</div>
        <div v-if="tienda.abierto !== null" class="tp-estado" :class="tienda.abierto ? 'tp-estado--abierto' : 'tp-estado--cerrado'">
          <span class="tp-dot"></span>
          {{ tienda.abierto ? `ABIERTO${tienda.horaCierre ? ' · HASTA ' + tienda.horaCierre : ''}` : 'CERRADO' }}
        </div>
        <div v-if="tienda.sucursales && tienda.sucursales.length > 1 && sucursalActual" class="tp-sucursal-actual">
          📍 {{ sucursalActual.nombre }} · <a href="#" @click.prevent="mostrarSelectorSucursal = true">cambiar</a>
        </div>
      </div>

      <!-- Tabs de categoría -->
      <div class="tp-tabs">
        <button class="tp-tab" :class="{ 'tp-tab--activo': categoriaActiva === null }" @click="categoriaActiva = null">Todo</button>
        <button v-for="c in categorias" :key="c" class="tp-tab" :class="{ 'tp-tab--activo': categoriaActiva === c }" @click="categoriaActiva = c">
          <img v-if="imagenCategoria(c)" :src="imagenCategoria(c)" class="tp-tab-img" />
          {{ c }}
        </button>
      </div>

      <!-- Buscador -->
      <div class="tp-search-wrap">
        <input v-model="busqueda" class="tp-search" placeholder="Buscar en el menú…" />
      </div>

      <!-- Grid de artículos -->
      <div class="tp-lista">
        <div v-if="!articulosFiltrados.length" class="tp-vacio">Sin resultados</div>
        <div v-for="a in articulosFiltrados" :key="a.id" class="tp-card">
          <div class="tp-card-img">
            <img v-if="a.imagenUrl" :src="a.imagenUrl" />
            <span v-else class="tp-card-img-fallback">📦</span>
            <span v-if="a.precioPromocional != null" class="tp-badge-oferta">🔥 Oferta</span>
            <span v-if="a.stock === 0" class="tp-badge-agotado">Agotado</span>
          </div>
          <div class="tp-card-body">
            <div class="tp-card-nombre">{{ a.nombre }}</div>
            <div v-if="a.gruposOpciones && a.gruposOpciones.length" class="tp-card-grupos">{{ a.gruposOpciones.length }} grupo(s) de opciones</div>
            <div v-if="a.descripcion" class="tp-card-desc">{{ a.descripcion }}</div>
            <div v-if="a.stock != null && a.stock > 0 && a.stock <= 5" class="tp-stock-bajo">¡Últimas {{ a.stock }} unidades!</div>
            <div class="tp-card-footer">
              <div v-if="a.precioPromocional != null">
                <span class="tp-card-precio-tachado">{{ a.moneda }} {{ Number(a.precio).toFixed(2) }}</span>
                <span class="tp-card-precio tp-card-precio--oferta">{{ a.moneda }} {{ Number(a.precioPromocional).toFixed(2) }}</span>
                <div class="tp-oferta-vence">Termina el {{ formatearFin(a.promocionFin) }}</div>
              </div>
              <span v-else class="tp-card-precio">{{ a.moneda }} {{ Number(a.precio).toFixed(2) }}</span>
              <button v-if="a.stock !== 0" class="tp-btn-agregar" @click="abrirDetalle(a)">+ Agregar</button>
              <span v-else class="tp-btn-agregar tp-btn-agregar--disabled">Agotado</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Carrito flotante -->
      <div v-if="carrito.items && carrito.items.length" class="tp-cart-bar" @click="vistaCarrito = true">
        <span>🛒 {{ totalUnidades }} artículo(s)</span>
        <span>Bs {{ Number(carrito.total).toFixed(2) }} · Ver carrito</span>
      </div>

      <!-- Bottom sheet: detalle / opciones del artículo -->
      <div v-if="articuloDetalle" class="tp-overlay" @click.self="articuloDetalle = null">
        <div class="tp-sheet">
          <div class="tp-sheet-header">
            <span>{{ articuloDetalle.nombre }}</span>
            <button class="tp-close" @click="articuloDetalle = null">✕</button>
          </div>
          <div class="tp-sheet-body">
            <p v-if="articuloDetalle.descripcion" class="tp-sheet-desc">{{ articuloDetalle.descripcion }}</p>

            <div v-for="g in articuloDetalle.gruposOpciones" :key="g.id" class="tp-grupo">
              <div class="tp-grupo-header">
                <span>{{ g.nombre }}</span>
                <span v-if="g.obligatorio" class="tp-obligatorio">Obligatorio</span>
              </div>
              <label v-for="o in g.opciones" :key="o.id" class="tp-opcion">
                <input
                  :type="g.tipo === 'unica' ? 'radio' : 'checkbox'"
                  :name="g.id"
                  :checked="seleccionIncluye(g.id, o.id)"
                  @change="toggleOpcion(g, o)"
                />
                <span class="tp-opcion-nombre">{{ o.nombre }}</span>
                <span v-if="o.precioExtra" class="tp-opcion-precio">+{{ o.precioExtra }}</span>
              </label>
            </div>

            <div class="tp-cantidad">
              <button @click="cantidadSel = Math.max(1, cantidadSel - 1)">−</button>
              <span>{{ cantidadSel }}</span>
              <button @click="cantidadSel++">+</button>
            </div>
          </div>
          <div class="tp-sheet-footer">
            <button class="tp-btn-primario" :disabled="agregando" @click="confirmarAgregar">
              {{ agregando ? 'Agregando…' : `Agregar · Bs ${precioSeleccion.toFixed(2)}` }}
            </button>
          </div>
        </div>
      </div>

      <!-- Carrito -->
      <div v-if="vistaCarrito" class="tp-overlay" @click.self="vistaCarrito = false">
        <div class="tp-sheet">
          <div class="tp-sheet-header">
            <span>Tu carrito</span>
            <button class="tp-close" @click="vistaCarrito = false">✕</button>
          </div>
          <div class="tp-sheet-body">
            <div v-if="!carrito.items.length" class="tp-vacio">Tu carrito está vacío</div>
            <div v-for="item in carrito.items" :key="item.id" class="tp-item-carrito">
              <div>
                <div class="tp-item-nombre">{{ item.cantidad }}x {{ item.nombre }}</div>
                <div v-if="item.opciones.length" class="tp-item-opciones">{{ item.opciones.map(o => o.opcionNombre).join(', ') }}</div>
              </div>
              <div class="tp-item-right">
                <span>Bs {{ ((item.precioBase + item.opciones.reduce((s, o) => s + o.precioExtra, 0)) * item.cantidad).toFixed(2) }}</span>
                <button class="tp-quitar" @click="quitarItem(item.id)">Quitar</button>
              </div>
            </div>
          </div>
          <div v-if="carrito.items.length" class="tp-sheet-footer tp-sheet-footer--total">
            <template v-if="metodosPagoDisponibles.length > 1">
              <div style="font-size:11px; font-weight:700; color:#374151;">¿Cómo pagás?</div>
              <div style="display:flex; gap:8px;">
                <button
                  v-for="m in metodosPagoDisponibles" :key="m"
                  class="tp-metodo-pago" :class="{ 'tp-metodo-pago--activo': metodoPagoSel === m }"
                  @click="metodoPagoSel = m"
                >{{ m === 'qr' ? '📱 QR' : '💵 Efectivo' }}</button>
              </div>
            </template>
            <div v-if="metodoPagoSel === 'qr' && sucursalActual && sucursalActual.qrImagenUrl" class="tp-qr-wrap">
              <img :src="sucursalActual.qrImagenUrl" class="tp-qr-img" />
              <div style="font-size:11px; color:#6b7280; text-align:center;">Escaneá para pagar y confirmá abajo</div>
            </div>
            <div class="tp-total-row"><span>Total</span><span>Bs {{ Number(carrito.total).toFixed(2) }}</span></div>
            <button class="tp-btn-primario" :disabled="confirmando" @click="confirmarPedido">{{ confirmando ? 'Enviando…' : 'Confirmar pedido' }}</button>
          </div>
        </div>
      </div>

      <!-- Pantalla de confirmación -->
      <div v-if="pedidoConfirmado" class="tp-overlay">
        <div class="tp-sheet tp-sheet--confirmacion">
          <div style="font-size:44px; margin-bottom:10px;">✅</div>
          <div style="font-weight:800; font-size:16px; margin-bottom:6px;">¡Pedido enviado!</div>
          <div v-if="carrito.codigoPedido" style="font-size:20px; font-weight:800; color:var(--tp-primario); margin-bottom:6px;">{{ carrito.codigoPedido }}</div>
          <div style="font-size:13px; opacity:.75; text-align:center; padding:0 20px 20px;">Seguimos coordinando tu pedido por WhatsApp.</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

const baseServer = (process.env.VUE_APP_BASE_SERVER || 'http://localhost:3001/api/');

export default {
  name: 'TiendaPublica',
  data() {
    return {
      cargando: true,
      error: '',
      tienda: {},
      articulos: [],
      categoriaActiva: null,
      busqueda: '',
      token: null,
      carrito: { items: [], total: 0 },
      articuloDetalle: null,
      seleccion: [],
      cantidadSel: 1,
      agregando: false,
      vistaCarrito: false,
      confirmando: false,
      pedidoConfirmado: false,

      sucursalId: null,
      mostrarSelectorSucursal: false,
      resolviendoUbicacion: false,
      errorUbicacion: '',
      metodoPagoSel: null,
    };
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    categorias() {
      // Primero las categorías reales creadas en el admin (con su orden e imagen), aunque
      // todavía no tengan ningún artículo asignado — y al final cualquier categoría "suelta"
      // que algún artículo tenga escrita pero que nunca se creó formalmente, para no esconder
      // productos por eso.
      const desdeCategorias = (this.tienda.categorias || []).map(c => c.nombre);
      const desdeArticulos = [...new Set(this.articulos.map(a => a.categoria).filter(Boolean))];
      const sueltas = desdeArticulos.filter(c => !desdeCategorias.includes(c));
      return [...desdeCategorias, ...sueltas];
    },
    articulosFiltrados() {
      const q = this.busqueda.trim().toLowerCase();
      return this.articulos.filter(a => {
        if (this.categoriaActiva && a.categoria !== this.categoriaActiva) return false;
        if (q && !a.nombre.toLowerCase().includes(q)) return false;
        return true;
      });
    },
    totalUnidades() {
      return (this.carrito.items || []).reduce((s, i) => s + i.cantidad, 0);
    },
    precioSeleccion() {
      if (!this.articuloDetalle) return 0;
      const extra = this.seleccion.reduce((s, sel) => {
        const grupo = this.articuloDetalle.gruposOpciones.find(g => g.id === sel.grupoId);
        const opcion = grupo && grupo.opciones.find(o => o.id === sel.opcionId);
        return s + (opcion ? Number(opcion.precioExtra) || 0 : 0);
      }, 0);
      const base = this.articuloDetalle.precioPromocional != null ? Number(this.articuloDetalle.precioPromocional) : Number(this.articuloDetalle.precio);
      return (base + extra) * this.cantidadSel;
    },
    sucursalActual() {
      if (!this.sucursalId) return null;
      return (this.tienda.sucursales || []).find(s => s.id === this.sucursalId) || null;
    },
    metodosPagoDisponibles() {
      if (!this.sucursalActual) return [];
      const metodos = [];
      if (this.sucursalActual.aceptaPagoQr) metodos.push('qr');
      if (this.sucursalActual.aceptaPagoEfectivo) metodos.push('efectivo');
      return metodos;
    },
  },
  async mounted() {
    await this.cargarTienda();
    if (this.error) return;
    await this.inicializarSesion();

    if (this.carrito.sucursalId) {
      // El carrito ya venía con sucursal (visita anterior guardada en localStorage) —
      // esa es la fuente de verdad, se respeta y se refresca el catálogo con ella.
      this.sucursalId = this.carrito.sucursalId;
      localStorage.setItem(this.sucursalStorageKey(), this.sucursalId);
      await this.cargarTienda(this.sucursalId);
      return;
    }

    if (!this.tienda.sucursales || !this.tienda.sucursales.length) return; // negocio de un solo local — nada más que hacer

    const guardada = localStorage.getItem(this.sucursalStorageKey());
    const valida = guardada && this.tienda.sucursales.some(s => s.id === guardada);
    if (valida) { await this.confirmarSucursal(guardada); return; }
    if (this.tienda.sucursales.length === 1) { await this.confirmarSucursal(this.tienda.sucursales[0].id); return; }

    this.mostrarSelectorSucursal = true; // varias sucursales, ninguna resuelta todavía — que elija
  },
  methods: {
    async cargarTienda(sucursalId) {
      this.cargando = true;
      try {
        const url = `${baseServer}tienda-publica/${this.slug}` + (sucursalId ? `?sucursalId=${sucursalId}` : '');
        const res = await axios.get(url);
        this.tienda = res.data.datos;
        this.articulos = this.tienda.articulos || [];
      } catch (e) {
        this.error = e?.response?.data?.mensaje || 'No encontramos esta tienda.';
      } finally {
        this.cargando = false;
      }
    },
    storageKey() {
      return `tienda_token_${this.slug}`;
    },
    sucursalStorageKey() {
      return `tienda_sucursal_${this.slug}`;
    },
    async inicializarSesion() {
      const deUrl = this.$route.query.s;
      const guardado = localStorage.getItem(this.storageKey());
      this.token = deUrl || guardado || null;

      if (this.token) {
        try {
          const res = await axios.get(`${baseServer}tienda-publica/${this.slug}/sesion/${this.token}`);
          this.carrito = res.data.datos;
          localStorage.setItem(this.storageKey(), this.token);
          if (this.carrito.estadoCarrito === 'confirmado') this.pedidoConfirmado = false; // deja ver el catálogo si vuelve
          return;
        } catch (e) {
          this.token = null; // token inválido/expirado — se crea uno nuevo abajo
        }
      }

      const res = await axios.post(`${baseServer}tienda-publica/${this.slug}/sesion`);
      this.token = res.data.datos.token;
      localStorage.setItem(this.storageKey(), this.token);
    },
    async confirmarSucursal(sucursalId) {
      this.sucursalId = sucursalId;
      localStorage.setItem(this.sucursalStorageKey(), sucursalId);
      this.mostrarSelectorSucursal = false;
      this.resolviendoUbicacion = false;
      this.errorUbicacion = '';
      await this.cargarTienda(sucursalId);
      try {
        const res = await axios.put(`${baseServer}tienda-publica/${this.slug}/sesion/${this.token}/sucursal`, { sucursalId });
        this.carrito = res.data.datos;
      } catch (e) {
        // El carrito ya tenía ítems de otra sucursal (poco probable en este punto, recién creado) — se ignora, sigue con la sucursal elegida igual para navegar el catálogo.
      }
    },
    usarUbicacion() {
      if (!navigator.geolocation) {
        this.errorUbicacion = 'Tu navegador no soporta geolocalización — elegí manualmente abajo.';
        return;
      }
      this.resolviendoUbicacion = true;
      this.errorUbicacion = '';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const cercana = this.sucursalMasCercana(pos.coords.latitude, pos.coords.longitude);
          if (cercana) {
            this.confirmarSucursal(cercana.id);
          } else {
            this.resolviendoUbicacion = false;
            this.errorUbicacion = 'No pudimos calcular la sucursal más cercana — elegí manualmente abajo.';
          }
        },
        () => {
          this.resolviendoUbicacion = false;
          this.errorUbicacion = 'No pudimos acceder a tu ubicación — elegí manualmente abajo.';
        },
        { timeout: 10000 },
      );
    },
    sucursalMasCercana(lat, lng) {
      const conCoords = (this.tienda.sucursales || []).filter(s => s.latitud != null && s.longitud != null);
      if (!conCoords.length) return null;
      let mejor = null;
      let mejorDistancia = Infinity;
      for (const s of conCoords) {
        const d = this.distanciaHaversine(lat, lng, s.latitud, s.longitud);
        if (d < mejorDistancia) { mejorDistancia = d; mejor = s; }
      }
      return mejor;
    },
    distanciaHaversine(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },
    imagenCategoria(nombre) {
      const c = (this.tienda.categorias || []).find(x => x.nombre === nombre);
      return c ? c.imagenUrl : null;
    },
    formatearFin(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString('es-BO', { timeZone: 'America/La_Paz', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    },
    seleccionIncluye(grupoId, opcionId) {
      return this.seleccion.some(s => s.grupoId === grupoId && s.opcionId === opcionId);
    },
    toggleOpcion(grupo, opcion) {
      if (grupo.tipo === 'unica') {
        this.seleccion = this.seleccion.filter(s => s.grupoId !== grupo.id);
        this.seleccion.push({ grupoId: grupo.id, opcionId: opcion.id });
      } else {
        const existe = this.seleccionIncluye(grupo.id, opcion.id);
        this.seleccion = existe
          ? this.seleccion.filter(s => !(s.grupoId === grupo.id && s.opcionId === opcion.id))
          : [...this.seleccion, { grupoId: grupo.id, opcionId: opcion.id }];
      }
    },
    abrirDetalle(articulo) {
      this.articuloDetalle = articulo;
      this.seleccion = [];
      this.cantidadSel = 1;
    },
    async confirmarAgregar() {
      const gruposSinElegir = (this.articuloDetalle.gruposOpciones || [])
        .filter(g => g.obligatorio && !this.seleccion.some(s => s.grupoId === g.id));
      if (gruposSinElegir.length) {
        alert(`Elegí una opción de "${gruposSinElegir[0].nombre}"`);
        return;
      }
      this.agregando = true;
      try {
        const res = await axios.post(`${baseServer}tienda-publica/${this.slug}/sesion/${this.token}/items`, {
          articuloId: this.articuloDetalle.id,
          cantidad: this.cantidadSel,
          opcionesSeleccionadas: this.seleccion,
        });
        this.carrito = res.data.datos;
        this.articuloDetalle = null;
      } catch (e) {
        alert(e?.response?.data?.mensaje || 'No se pudo agregar al carrito');
      } finally {
        this.agregando = false;
      }
    },
    async quitarItem(itemId) {
      try {
        const res = await axios.delete(`${baseServer}tienda-publica/${this.slug}/sesion/${this.token}/items/${itemId}`);
        this.carrito = res.data.datos;
      } catch (e) { /* silencioso */ }
    },
    async confirmarPedido() {
      let metodoPago = this.metodoPagoSel;
      if (!metodoPago && this.metodosPagoDisponibles.length === 1) metodoPago = this.metodosPagoDisponibles[0];
      if (this.metodosPagoDisponibles.length > 1 && !metodoPago) {
        alert('Elegí cómo vas a pagar antes de confirmar');
        return;
      }

      this.confirmando = true;
      try {
        const res = await axios.post(`${baseServer}tienda-publica/${this.slug}/sesion/${this.token}/confirmar`, { metodoPago: metodoPago || undefined });
        this.carrito = res.data.datos;
        this.vistaCarrito = false;
        this.pedidoConfirmado = true;
        localStorage.removeItem(this.storageKey());
      } catch (e) {
        alert(e?.response?.data?.mensaje || 'No se pudo confirmar el pedido');
      } finally {
        this.confirmando = false;
      }
    },
  },
};
</script>

<style scoped>
.tp-page {
  min-height: 100vh;
  background: #f5f8fd;
  color: #182233;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-bottom: 90px;
}
.tp-loading, .tp-error {
  min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 24px;
}
.tp-spinner {
  width: 32px; height: 32px; border: 3px solid color-mix(in srgb, var(--tp-primario) 20%, white); border-top-color: var(--tp-primario); border-radius: 50%;
  animation: tp-spin 0.8s linear infinite;
}
@keyframes tp-spin { to { transform: rotate(360deg); } }

.tp-cover { position: relative; height: 150px; background: var(--tp-primario) center/cover no-repeat; }
.tp-cover-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.35)); }
.tp-logo {
  position: absolute; bottom: -24px; left: 16px; width: 56px; height: 56px; border-radius: 14px;
  border: 3px solid #fff; object-fit: cover; background: #fff;
}
.tp-header { padding: 34px 16px 12px; }
.tp-nombre { font-size: 19px; font-weight: 800; }
.tp-estado {
  display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 800;
  letter-spacing: .04em; padding: 4px 9px; border-radius: 20px; margin-top: 6px;
}
.tp-estado--abierto { background: #dcfce7; color: #16a34a; }
.tp-estado--cerrado { background: #fee2e2; color: #dc2626; }
.tp-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

.tp-tabs { display: flex; gap: 8px; overflow-x: auto; padding: 0 16px 10px; -webkit-overflow-scrolling: touch; }
.tp-tab {
  flex-shrink: 0; background: #fff; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white); border-radius: 20px; padding: 7px 14px;
  font-size: 12px; font-weight: 600; color: #6b7280; cursor: pointer; font-family: inherit;
}
.tp-tab--activo { background: var(--tp-primario); border-color: var(--tp-primario); color: #fff; }

.tp-search-wrap { padding: 0 16px 14px; }
.tp-search {
  width: 100%; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white); border-radius: 12px; padding: 10px 14px; font-size: 13px;
  font-family: inherit; background: #fff; box-sizing: border-box;
}
.tp-search:focus { outline: none; border-color: var(--tp-primario); }

.tp-lista { display: flex; flex-direction: column; gap: 12px; padding: 0 16px; }
.tp-vacio { text-align: center; padding: 40px 0; color: #9ca3af; font-size: 13px; }
.tp-card { background: #fff; border-radius: 16px; overflow: hidden; display: flex; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.tp-card-img { position: relative; width: 96px; height: 96px; flex-shrink: 0; background: color-mix(in srgb, var(--tp-primario) 8%, white); display: flex; align-items: center; justify-content: center; }
.tp-card-img img { width: 100%; height: 100%; object-fit: cover; }
.tp-card-img-fallback { font-size: 26px; }
.tp-card-body { flex: 1; padding: 10px 12px; min-width: 0; }
.tp-card-nombre { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
.tp-card-grupos { font-size: 10px; color: var(--tp-primario); margin-bottom: 2px; }
.tp-card-desc { font-size: 11px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 6px; }
.tp-card-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; }
.tp-card-precio { font-size: 13px; font-weight: 800; }
.tp-btn-agregar {
  background: color-mix(in srgb, var(--tp-primario) 8%, white); color: var(--tp-primario); border: none; border-radius: 10px; padding: 6px 12px;
  font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit;
}

.tp-cart-bar {
  position: fixed; left: 16px; right: 16px; bottom: 16px; background: var(--tp-primario); color: #fff;
  border-radius: 14px; padding: 13px 18px; display: flex; align-items: center; justify-content: space-between;
  font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 18px color-mix(in srgb, var(--tp-primario) 35%, transparent); z-index: 40;
}

.tp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: flex-end; z-index: 100; }
.tp-sheet {
  background: #fff; width: 100%; max-height: 85vh; border-radius: 20px 20px 0 0; display: flex; flex-direction: column;
  overflow: hidden;
}
.tp-sheet--confirmacion { align-items: center; padding: 40px 20px; border-radius: 20px; margin: auto 16px; }
.tp-sheet-header {
  display: flex; align-items: center; justify-content: space-between; padding: 16px 18px;
  border-bottom: 1px solid color-mix(in srgb, var(--tp-primario) 8%, white); font-weight: 800; font-size: 14px;
}
.tp-close { background: none; border: none; font-size: 16px; color: #9ca3af; cursor: pointer; }
.tp-sheet-body { padding: 16px 18px; overflow-y: auto; }
.tp-sheet-desc { font-size: 12px; color: #6b7280; margin-bottom: 14px; }
.tp-grupo { margin-bottom: 16px; }
.tp-grupo-header { display: flex; align-items: center; justify-content: space-between; font-size: 12px; font-weight: 700; margin-bottom: 8px; }
.tp-obligatorio { font-size: 9px; background: #fef3c7; color: #b45309; padding: 2px 7px; border-radius: 10px; font-weight: 700; }
.tp-opcion { display: flex; align-items: center; gap: 10px; padding: 8px 0; font-size: 13px; cursor: pointer; }
.tp-opcion-nombre { flex: 1; }
.tp-opcion-precio { color: var(--tp-primario); font-weight: 700; font-size: 12px; }
.tp-cantidad { display: flex; align-items: center; gap: 16px; justify-content: center; margin-top: 8px; }
.tp-cantidad button {
  width: 32px; height: 32px; border-radius: 50%; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white); background: #fff;
  font-size: 16px; cursor: pointer; color: var(--tp-primario);
}
.tp-cantidad span { font-weight: 800; font-size: 15px; min-width: 20px; text-align: center; }
.tp-sheet-footer { padding: 14px 18px; border-top: 1px solid color-mix(in srgb, var(--tp-primario) 8%, white); }
.tp-sheet-footer--total { display: flex; flex-direction: column; gap: 10px; }
.tp-total-row { display: flex; justify-content: space-between; font-weight: 800; font-size: 14px; }
.tp-btn-primario {
  width: 100%; background: var(--tp-primario); color: #fff; border: none; border-radius: 12px; padding: 13px;
  font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit;
}
.tp-btn-primario:disabled { opacity: .6; cursor: default; }

.tp-item-carrito { display: flex; align-items: flex-start; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f5f8fd; gap: 10px; }
.tp-item-nombre { font-size: 13px; font-weight: 700; }
.tp-item-opciones { font-size: 11px; color: #9ca3af; margin-top: 2px; }
.tp-item-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.tp-quitar { background: none; border: none; color: #dc2626; font-size: 11px; cursor: pointer; font-family: inherit; }

.tp-tab-img { width: 16px; height: 16px; border-radius: 50%; object-fit: cover; margin-right: 4px; vertical-align: middle; }

.tp-badge-oferta {
  position: absolute; top: 6px; left: 6px; background: #f97316; color: #fff;
  font-size: 9px; font-weight: 800; padding: 2px 6px; border-radius: 4px;
}
.tp-card-precio-tachado { font-size: 11px; color: #9ca3af; text-decoration: line-through; margin-right: 6px; }
.tp-card-precio--oferta { color: #f97316; }
.tp-oferta-vence { font-size: 9px; color: #f97316; margin-top: 2px; }

.tp-selector { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; }
.tp-selector-inner { width: 100%; max-width: 360px; text-align: center; }
.tp-sucursal-opcion {
  width: 100%; text-align: left; background: #fff; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white);
  border-radius: 12px; padding: 12px 14px; cursor: pointer; font-family: inherit;
}
.tp-sucursal-opcion:hover { border-color: var(--tp-primario); }
.tp-sucursal-actual { font-size: 11px; color: #6b7280; margin-top: 6px; }
.tp-sucursal-actual a { color: var(--tp-primario); font-weight: 700; }

.tp-badge-agotado {
  position: absolute; bottom: 4px; left: 4px; right: 4px; background: rgba(0,0,0,.65); color: #fff;
  font-size: 9px; font-weight: 700; text-align: center; padding: 2px 0; border-radius: 4px;
}
.tp-stock-bajo { font-size: 10px; color: #f97316; font-weight: 700; margin-bottom: 4px; }
.tp-btn-agregar--disabled { background: #f3f4f6; color: #9ca3af; cursor: default; }

.tp-metodo-pago {
  flex: 1; background: #fff; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white); border-radius: 10px;
  padding: 10px; font-size: 12px; font-weight: 700; color: #6b7280; cursor: pointer; font-family: inherit;
}
.tp-metodo-pago--activo { background: var(--tp-primario); border-color: var(--tp-primario); color: #fff; }
.tp-qr-wrap { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 10px 0; }
.tp-qr-img { width: 180px; height: 180px; object-fit: contain; border: 1px solid color-mix(in srgb, var(--tp-primario) 20%, white); border-radius: 12px; background: #fff; }
</style>
